import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Box,
  Chip,
  CircularProgress
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import API_BASE_URL from '../config/api';

const QuestionList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    examType: '',
    topic: '',
    difficulty: '',
    questionType: ''
  });
  const [sortBy, setSortBy] = useState('default');
  const [filterOptions, setFilterOptions] = useState({
    examTypes: [],
    topics: [],
    difficulties: [],
    questionTypes: []
  });

  useEffect(() => {
    fetchFilterOptions();
    fetchQuestions();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [filters, sortBy, user]);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/questions/meta/filters`);
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      // Single endpoint - backend will add completion status if user is logged in
      const response = await axios.get(
        `${API_BASE_URL}/api/questions?${params.toString()}`,
        {
          withCredentials: true // Include credentials for session-based auth
        }
      );
      
      let fetchedQuestions = response.data.questions || [];
      
      // Sort questions based on sortBy value
      if (sortBy === 'completed') {
        fetchedQuestions = [...fetchedQuestions].sort((a, b) => {
          if (a.isCompleted && !b.isCompleted) return -1;
          if (!a.isCompleted && b.isCompleted) return 1;
          return 0;
        });
      } else if (sortBy === 'not-completed') {
        fetchedQuestions = [...fetchedQuestions].sort((a, b) => {
          if (!a.isCompleted && b.isCompleted) return -1;
          if (a.isCompleted && !b.isCompleted) return 1;
          return 0;
        });
      }
      
      setQuestions(fetchedQuestions);
    } catch (error) {
      console.error('Error fetching questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'success';
      case 'Medium': return 'warning';
      case 'Hard': return 'error';
      default: return 'default';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ marginTop: 4, marginBottom: 4 }}>
      <Typography variant="h4" gutterBottom>
        Practice Questions
      </Typography>

      {/* Filters and Sort */}
      <Box sx={{ marginBottom: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Exam Type</InputLabel>
          <Select
            value={filters.examType}
            label="Exam Type"
            onChange={(e) => handleFilterChange('examType', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {filterOptions.examTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Topic</InputLabel>
          <Select
            value={filters.topic}
            label="Topic"
            onChange={(e) => handleFilterChange('topic', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {filterOptions.topics.map(topic => (
              <MenuItem key={topic} value={topic}>{topic}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Difficulty</InputLabel>
          <Select
            value={filters.difficulty}
            label="Difficulty"
            onChange={(e) => handleFilterChange('difficulty', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {filterOptions.difficulties.map(diff => (
              <MenuItem key={diff} value={diff}>{diff}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Question Type</InputLabel>
          <Select
            value={filters.questionType}
            label="Question Type"
            onChange={(e) => handleFilterChange('questionType', e.target.value)}
          >
            <MenuItem value="">All</MenuItem>
            {filterOptions.questionTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {user && (
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortBy}
              label="Sort By"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="completed">Completed First</MenuItem>
              <MenuItem value="not-completed">Not Completed First</MenuItem>
            </Select>
          </FormControl>
        )}
      </Box>

      {/* Questions Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {questions.length === 0 ? (
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary" align="center">
                No questions found. Try adjusting your filters.
              </Typography>
            </Grid>
          ) : (
            questions.map((question) => (
              <Grid item xs={12} sm={6} md={4} key={question.questionId}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    position: 'relative',
                    border: question.isCompleted ? '2px solid' : 'none',
                    borderColor: question.isCompleted ? 'success.main' : 'transparent',
                    '&:hover': {
                      boxShadow: 6
                    }
                  }}
                  onClick={() => navigate(`/question/${question.questionId}`)}
                >
                  {question.isCompleted && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1
                      }}
                    >
                      <CheckCircleIcon color="success" />
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', gap: 1, marginBottom: 2, flexWrap: 'wrap' }}>
                      <Chip label={question.examType} size="small" color="primary" />
                      <Chip
                        label={question.difficulty}
                        size="small"
                        color={getDifficultyColor(question.difficulty)}
                      />
                      <Chip label={question.topic} size="small" variant="outlined" />
                      {question.isCompleted && (
                        <Chip 
                          label="Completed" 
                          size="small" 
                          color="success" 
                          icon={<CheckCircleIcon />}
                        />
                      )}
                    </Box>
                    <Typography variant="h6" gutterBottom>
                      {question.questionText.substring(0, 100)}
                      {question.questionText.length > 100 ? '...' : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Type: {question.questionType}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}
    </Container>
  );
};

export default QuestionList;

